import { ExpressCheckoutRenderer } from '../expressPayment/ExpressCheckoutRenderer/ExpressCheckoutRenderer';
import { PaymentMethods } from '../PaymentMethods/PaymentMethods';
import { PaymentMethodForm } from '../PaymentMethodForm/PaymentMethodForm';
import { ApprovalText } from '../PaymentInputs/ApprovalText';
import { Footer } from '../Footer/Footer';
import { DesktopViewer } from '../Common/Viewer';
import { Error } from '../Common/Error';
import { useTemplateFeatures } from '@/hooks/useTemplateFeatures';
import { useContext, useEffect, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { cn } from '@/utils/cn';
import { PAYMENT_STYLES, getPaymentContainerStyles } from '@/constants/styles';
import { Templates } from '@/types/common';
import { logInfo } from '@/utils/logger';
import { InfoLogs } from '@/constants/logs';
import { getLogInfoData } from '@/utils/logs';
import { useCheckout } from '@/hooks/useCheckout';
import { trackCheckoutPresented } from '@/utils/checkoutMixpanelTracking';
import { getSessionIdFromUrlParams } from '@/utils/location';
import { isLandscape } from '@/utils/device';
import { usePaymentSectionVisibility } from '@/hooks/usePaymentSectionVisibility';
import { shouldShowApprovalTextForPaymentMethod } from '@/utils/payment';
import { useIsMobile } from '@/hooks/useIsMobile';

export const Payment = () => {
  const {
    shouldShowExpressCheckout: shouldShowExpressCheckoutTemplate,
    shouldShowPaymentMethods: shouldShowPaymentMethodsTemplate,
    shouldShowDesktopViewer,
    shouldShowPaymentMethodForm,
  } = useTemplateFeatures();
  const { shouldShowExpressCheckoutByScreen, shouldShowPaymentMethodsByScreen } = usePaymentSectionVisibility();
  const isInitialized = useRef(false);
  const context = useContext(CheckoutContext);
  const sessionId = getSessionIdFromUrlParams();
  const [blockedForZipCode, setBlockedForZipCode] = useState(false);
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  const isMobile = useIsMobile();

  const checkout = useCheckout();
  const {
    template,
    paymentMethodSelected,
    payment: { isInPaymentProcess, isExpressPaymentInProcess },
  } = context;
  const isOnePayment = template.type === Templates.OnePayment;
  const shouldShowApprovalText = shouldShowApprovalTextForPaymentMethod(paymentMethodSelected) && !blockedForZipCode;
  const containerClassName = cn(
    PAYMENT_STYLES.base,
    getPaymentContainerStyles(template.type),
    isOnePayment ? 'justify-between h-full' : 'justify-between gap-10'
  );

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    trackCheckoutPresented(sessionId, context);
    logInfo(InfoLogs.checkoutPresented, getLogInfoData(context));
  }, []);

  const shouldShowExpressCheckout = shouldShowExpressCheckoutTemplate && shouldShowExpressCheckoutByScreen;
  const shouldShowPaymentMethods = shouldShowPaymentMethodsTemplate && shouldShowPaymentMethodsByScreen;

  return (
    <div
      data-testid="payment-section"
      className={cn(
        containerClassName,
        !isLandscape() && 'md:h-dvh md:min-h-0 md:overflow-y-auto md:overflow-x-hidden'
      )}
    >
      <div
        className={cn(
          'flex flex-col gap-[18px] flex-1 min-h-0 md:max-w-90 md:w-full md:my-0 landscape:flex-none landscape:w-full landscape:max-w-full landscape:mt-0 landscape:my-0',
          isOnePayment ? '' : 'md:mt-15',
          (isInPaymentProcess || isExpressPaymentInProcess) && 'pointer-events-none',
          isLandscape() && 'h-auto',
          ((!isOnePayment && !isLandscape()) || (isOnePayment && !isMobile)) && 'md:overflow-hidden min-h-fit'
        )}
      >
        {shouldShowExpressCheckout && (
          <ExpressCheckoutRenderer
            onDisabledButtonClicked={() => {
              setBlockedForZipCode(true);
            }}
            blockedForZipCode={blockedForZipCode}
            isZipCodeValid={isZipCodeValid}
          />
        )}
        {shouldShowPaymentMethods && !blockedForZipCode && <PaymentMethods />}
        {shouldShowPaymentMethodForm && (
          <PaymentMethodForm
            blockedForZipCode={blockedForZipCode}
            setBlockedForZipCode={setBlockedForZipCode}
            setIsZipCodeValid={setIsZipCodeValid}
          />
        )}
        {shouldShowDesktopViewer && <DesktopViewer />}
        {shouldShowApprovalText && <ApprovalText withPadding={false} />}
        <Error />
      </div>

      <div className={cn('w-full shrink-0')}>{!checkout.dataIsReady && <Footer />}</div>
    </div>
  );
};
